import {
  formHelperTextClasses,
  inputBaseClasses,
  inputLabelClasses,
  outlinedInputClasses,
  autocompleteClasses,
  type TypographyProps,
  type Theme,
} from '@mui/material';
import { textVariant } from 'components/text';

export const getInputStyles = ({
  theme,
  labelSeparate = true,
  isUnderline,
  labelColor,
  labelVariant = 'labelSm',
  labelWeight = 'regular',
  variant,
}: {
  theme: Theme;
  labelSeparate?: boolean;
  isUnderline?: boolean;
  labelColor?: TypographyProps['color'];
  labelVariant?: TypographyProps['variant'];
  labelWeight?: TypographyProps['textVariant'];
  variant?: 'outlined' | 'standard' | 'filled' | 'unstyled';
}) => {
  const isOutlined = variant === 'outlined';

  const labelStyleBase =
    (theme.typography?.[labelVariant as keyof typeof theme.typography] as Record<string, string>) ??
    {};

  const resolvedLabelStyle = typeof labelStyleBase === 'string' ? {} : labelStyleBase;
  const resolvedTextVariant = textVariant?.[labelWeight] ?? {};
  const underlineStyle = isUnderline ? { textDecoration: 'underline' } : {};

  const sharedLabelStyles = {
    ...(resolvedLabelStyle ?? {}),
    ...resolvedTextVariant,
    ...underlineStyle,
  };

  return {
    [`& .${inputLabelClasses.error}`]: {
      color: theme.palette.text.error,
    },
    '& .MuiInput-root': {
      color: theme.palette.text.secondary,
    },
    [`& .${inputLabelClasses.root}`]: {
      ...sharedLabelStyles,
      lineHeight: '20px',
      color: labelColor ?? theme.palette.text.placeholder,
      [`&.${inputLabelClasses.focused}`]: {
        color: theme?.palette.text.placeholder,
      },
    },
    [`& .${inputLabelClasses.asterisk}`]: {
      color: theme.palette.text.error,
    },
    [`& .${formHelperTextClasses.root}`]: {
      margin: '0.5px',
      ...theme.typography.bodySm,
      color: theme.palette.text.tertiary,
      lineHeight: '20px',
      [`&.${formHelperTextClasses.error}`]: {
        color: theme.palette.text.error,
      },
    },
    '.MuiSelect-select': {
      fontSize: '0.875rem',
    },

    [`& .${autocompleteClasses.popupIndicator}`]: {
      alignItems: 'baseline',
      backgroundColor: 'transparent !important',
      padding: 0,
    },
    [`& .${autocompleteClasses.inputRoot}`]: {
      '&.MuiInputBase-root': {
        maxHeight: 'unset',
      },
    },
    [`& .${autocompleteClasses.popupIndicatorOpen}`]: {
      alignItems: 'center',
    },

    [`& .${autocompleteClasses.endAdornment}`]: {
      top: 'unset',
    },

    ...(isOutlined && labelSeparate
      ? {
          '&&& .MuiAutocomplete-input, input': {
            borderRadius: '8px',
            height: '24px',
            padding: '10px 14px',
            color: theme.palette.text.primary,
            '&::placeholder, &::-webkit-input-placeholder, &::-moz-placeholder, &:-ms-input-placeholder, &::-ms-input-placeholder':
              {
                ...theme.typography.bodyMd,
                lineHeight: '24px',
                opacity: 1,
                color: theme.palette.text.placeholder,
              },
          },

          legend: {
            maxWidth: '0px',
          },
          [`& .${inputLabelClasses.outlined}`]: {
            position: 'unset',
            transform: 'none',
            transition: 'none',
          },

          [`& .${outlinedInputClasses.root}`]: {
            borderRadius: '8px',
            marginBottom: '6px',
            marginTop: '6px',
            backgroundColor: theme.palette.bg.primary,
            maxHeight: '44px',
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              borderRadius: '8px',
              borderColor: theme.palette.border.primary,
              color: theme.palette.text.quaternary,
              borderWidth: '0.5px',
            },
            '&:hover': {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: theme.palette.border.brand,
              },
            },
            [`&.${outlinedInputClasses.focused}`]: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: theme.palette.border.brand,
                borderWidth: '1px',
                boxShadow:
                  '0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(22, 105, 99, 0.24)',
              },
            },
            [`&.${outlinedInputClasses.error}`]: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderWidth: '0.5px',
                borderColor: theme.palette.border.error,
                boxShadow: 'none',
              },
              '&:hover': {
                [`& .${outlinedInputClasses.notchedOutline}`]: {
                  borderColor: theme.palette.border.errorSolid,
                },
              },
            },
            [`&.${outlinedInputClasses.error}.${outlinedInputClasses.focused}`]: {
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: theme.palette.border.errorSolid,
                borderWidth: '1px',
              },
            },
          },
          [`& .${inputBaseClasses.root}`]: {
            [`&.${inputBaseClasses.disabled}`]: {
              backgroundColor: theme.palette.bg.disabledSubtle,
            },
          },
          input: { borderRadius: '8px' },
        }
      : {
          '.MuiFormLabel-root:not(.MuiFormLabel-filled)': {
            fontSize: '0.875rem',
            transform: 'translate(14px, 6px) scale(1)',
          },
        }),
  };
};
